import type { JobListOrdering, JobState } from 'types/jobs';

export const JOB_TABS: JobState[] = ['published', 'drafts', 'expired', 'archived'];

export const JOB_TAB_DISPLAY_ORDER: JobState[] = ['published', 'expired', 'drafts', 'archived'];

export const JOBS_TAB_STORAGE_KEY = 'jobs_tab';
export const JOBS_ORDER_STORAGE_KEY = 'jobs-list-order';
export const JOBS_SEARCH_STORAGE_KEY = 'jobs-list-search';

/** Tolerates a missing or corrupt stored value rather than yielding NaN. */
export const parseStoredTab = (stored: string | null) => {
  const parsed = Number(stored);
  return Number.isInteger(parsed) && parsed >= 0 && parsed < JOB_TABS.length ? parsed : 0;
};

export const JOB_ORDERING_OPTIONS: Record<JobState, JobListOrdering[]> = {
  published: [
    '-date_published',
    'date_published',
    'deadline',
    '-deadline',
    'title',
    '-title',
    '-date_created',
    'date_created',
  ],
  drafts: ['-date_created', 'date_created', 'title', '-title'],
  expired: ['-deadline', 'deadline', '-date_published', 'date_published', 'title', '-title'],
  archived: ['-date_archived', 'date_archived', 'title', '-title', '-date_created', 'date_created'],
};

/** Both directions share one short label, so the button reads it from either. */
export const JOB_ORDERING_SHORT_KEY: Record<JobListOrdering, keyof typeof jobsLocale.en> = {
  title: 'sortShort_title',
  '-title': 'sortShort_title',
  date_created: 'sortShort_date_created',
  '-date_created': 'sortShort_date_created',
  date_published: 'sortShort_date_published',
  '-date_published': 'sortShort_date_published',
  deadline: 'sortShort_deadline',
  '-deadline': 'sortShort_deadline',
  date_archived: 'sortShort_date_archived',
  '-date_archived': 'sortShort_date_archived',
};

/** Kept separate from the menu order so reordering the menu can't change a tab's default. */
export const DEFAULT_JOB_ORDERING: Record<JobState, JobListOrdering> = {
  published: '-date_published',
  drafts: '-date_created',
  expired: '-deadline',
  archived: '-date_archived',
};

/** A stored ordering can outlive its option, and mobile has no sort control to show one. */
export const resolveJobOrdering = ({
  status,
  stored,
  isMobile,
}: {
  status: JobState;
  stored?: JobListOrdering;
  isMobile: boolean;
}): JobListOrdering =>
  !isMobile && stored && JOB_ORDERING_OPTIONS[status].includes(stored)
    ? stored
    : DEFAULT_JOB_ORDERING[status];

/** The least-used columns drop first on narrow screens. */
export const COLUMN_BREAKPOINTS = {
  daysOpen: 800,
  rejected: 850,
  hired: 900,
  daysLeft: 900,
  retracted: 950,
  access: 1050,
} as const;

/** Rows re-render only when a column appears or disappears, not on every resize. */
export const snapToColumnBreakpoint = (width: number) =>
  Math.max(
    0,
    ...Object.values(COLUMN_BREAKPOINTS)
      .filter((breakpoint) => width > breakpoint)
      .map((breakpoint) => breakpoint + 1),
  );

export const DEADLINE_WARNING_DAYS = 8;

export const jobsLocale = {
  en: {
    state_published: 'Published',
    state_scheduled: 'Scheduled',
    state_draft: 'Draft',
    state_expired: 'Expired',
    state_archived: 'Archived',
    sort_title: 'Alphabetical • A → Z',
    sortShort_title: 'Alphabetical',
    'sort_-title': 'Alphabetical • Z → A',
    'sort_-date_created': 'Created • Newest',
    sort_date_created: 'Created • Oldest',
    sortShort_date_created: 'Created',
    'sort_-date_published': 'Published • Newest',
    sort_date_published: 'Published • Oldest',
    sortShort_date_published: 'Published',
    sort_deadline: 'Deadline • Soonest',
    sortShort_deadline: 'Deadline',
    'sort_-deadline': 'Deadline • Latest',
    'sort_-date_archived': 'Archived • Newest',
    sort_date_archived: 'Archived • Oldest',
    sortShort_date_archived: 'Archived',
    privateJob: 'Private Job',
    count_new: 'New',
    count_applicants: 'Applicants',
    count_active: 'Active',
    count_retracted: 'Retracted',
    count_rejected: 'Rejected',
    count_hired: 'Hired',
    count_daysOpen: 'Days open',
    count_daysLeft: 'Days left',
    count_access: 'Access',
    loadError: 'Could not load jobs',
    loadErrorSubtitle: 'Something went wrong reaching the server. Please try again',
    loadErrorRetry: 'Try again',
    loadErrorOffline: 'You seem to be offline, please check your connection and try again',
  },
  is: {
    state_published: 'Í loftinu',
    state_scheduled: 'Verður birt',
    state_draft: 'Drög',
    state_expired: 'Útrunnið',
    state_archived: 'Í geymslu',
    sort_title: 'Stafrófsröð • A → Ö',
    sortShort_title: 'Stafrófsröð',
    'sort_-title': 'Stafrófsröð • Ö → A',
    'sort_-date_created': 'Stofnað • Nýjast',
    sort_date_created: 'Stofnað • Elst',
    sortShort_date_created: 'Stofnað',
    'sort_-date_published': 'Birt • Nýjast',
    sort_date_published: 'Birt • Elst',
    sortShort_date_published: 'Birt',
    sort_deadline: 'Umsóknarfrestur • Fyrst',
    sortShort_deadline: 'Umsóknarfrestur',
    'sort_-deadline': 'Umsóknarfrestur • Síðast',
    'sort_-date_archived': 'Sett í geymslu • Nýjast',
    sort_date_archived: 'Sett í geymslu • Elst',
    sortShort_date_archived: 'Sett í geymslu',
    privateJob: 'Falið starf',
    count_new: 'Nýjar',
    count_applicants: 'Umsóknir',
    count_active: 'Virkar',
    count_retracted: 'Afturkallaðar',
    count_rejected: 'Hafnað',
    count_hired: 'Ráðnar',
    count_daysOpen: 'Dagar úti',
    count_daysLeft: 'Dagar eftir',
    count_access: 'Aðgangur',
    loadError: 'Ekki tókst að sækja störf',
    loadErrorSubtitle:
      'Eitthvað fór úrskeiðis í samskiptum við vefþjóninn. Vinsamlegast prófaðu aftur',
    loadErrorRetry: 'Prófa aftur',
    loadErrorOffline: 'Þú virðist vera án nettengingar, athugaðu tenginguna og reyndu aftur',
  },
};
